import { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, RefreshCw, Upload, Plus, Search, Filter,
    ChevronDown, Loader2, ClipboardList, History, X, Save, Lock, Unlock,
    RotateCcw, CheckSquare, Layers, SlidersHorizontal, ChevronUp, Trash2
} from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { useNotification } from '@/context/NotificationContext';
import { apiGet, apiPost, apiPatch, apiDelete } from '../api';

export default function WorklistRunnerPage({ appId, onBack, onEdit, isPreview = false }) {
    const { token, user } = useAuth();
    const { addNotification } = useNotification();
    // Only admins (r1=Super Admin, r2=Tenant Admin, r3=Dept Admin) can pull/sync
    const isAdmin = ['r1', 'r2', 'r3'].includes(user?.roleId || '');

    const [app, setApp]           = useState(null);
    const [items, setItems]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [searchVal, setSearchVal] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    // Default "Assigned to Me" ON for lower-privilege roles (Tenant User and below)
    const defaultMyItems = ['r7', 'r8', 'r9', 'r10'].includes(user?.roleId || '');
    const [myItems, setMyItems]   = useState(defaultMyItems);
    const [pulling, setPulling]   = useState(false);
    const [writing, setWriting]   = useState(false);
    const [drawer, setDrawer]     = useState(null);   // { item, audit }
    const [newItemForm, setNewItemForm] = useState(null);
    const [reviewModal, setReviewModal] = useState(false); // Pending changes review
    const [page, setPage]         = useState(1);
    const [total, setTotal]       = useState(0);
    const PAGE_SIZE = 50;
    const [platformUsers, setPlatformUsers] = useState([]);
    // Multi-select
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds]     = useState(new Set());
    const [massUpdateOpen, setMassUpdateOpen] = useState(false);
    // Dynamic column filters: [{id, col, op, value}]
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    // Column sort — init from per-user localStorage pref, app default applied after load
    const lsKey = `worklist_sort_${appId}`;
    const [sortCol, setSortCol] = useState(() => {
        try { return JSON.parse(localStorage.getItem(lsKey))?.col || null; } catch { return null; }
    });
    const [sortDir, setSortDir] = useState(() => {
        try { return JSON.parse(localStorage.getItem(lsKey))?.dir || 'asc'; } catch { return 'asc'; }
    });
    const [viewSaved, setViewSaved] = useState(false);
    // Inline revert confirm: itemId string or null
    const [revertConfirmId, setRevertConfirmId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [appData, itemsData] = await Promise.all([
                apiGet(`/worklists/${appId}`, token),
                apiGet(`/worklists/${appId}/items?page=${page}&page_size=${PAGE_SIZE}${statusFilter ? `&status=${statusFilter}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`, token),
            ]);
            setApp(appData);
            setItems(itemsData.items || []);
            setTotal(itemsData.total || 0);
        } catch { addNotification('Failed to load worklist', 'error'); }
        finally { setLoading(false); }
    }, [appId, token, page, statusFilter, search]);

    useEffect(() => { load(); }, [load]);

    // Apply app-level default sort once the app config is loaded
    // Only applies when the user has no saved local preference
    useEffect(() => {
        if (!app) return;
        const saved = (() => { try { return JSON.parse(localStorage.getItem(lsKey)); } catch { return null; } })();
        const appDefault = app.default_sort_col || '';
        if (!saved?.col && appDefault) {
            setSortCol(appDefault);
            setSortDir(app.default_sort_dir || 'asc');
        }
    }, [app?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce search typing
    useEffect(() => {
        const t = setTimeout(() => {
            if (search !== searchVal) {
                setSearch(searchVal);
                setPage(1);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [searchVal, search]);

    // Fetch platform users for assignee dropdown
    useEffect(() => {
        if (!token) return;
        apiGet('/users/', token)
            .then(users => {
                const names = (users || []).map(u => u.email || u.name).filter(Boolean);
                setPlatformUsers(names);
            })
            .catch(() => {});
    }, [token]);

    const handlePull = async (mode = 'upsert') => {
        setPulling(true);
        try {
            const stats = await apiPost(`/worklists/${appId}/pull`, token, { upsert_mode: mode });
            addNotification(`Pull complete — ${stats.inserted} new, ${stats.updated} updated`, 'success');
            load();
        } catch { addNotification('Pull failed', 'error'); }
        finally { setPulling(false); }
    };

    const handleWriteback = async (dryRun = false, config = {}) => {
        setWriting(true);
        try {
            const payload = {
                dry_run:                 dryRun,
                item_ids:                config.itemIds?.length ? config.itemIds : undefined,
                writeback_mode_override: config.writeMode || undefined,
                included_fields:         config.fields?.length ? config.fields : undefined,
                force_write:             config.forceWrite || false,
            };
            const result = await apiPost(`/worklists/${appId}/writeback`, token, payload);
            if (dryRun) {
                addNotification(`Dry run: ${result.count} items would be synced`, 'info');
            } else {
                const conflictMsg = result.conflicts > 0 ? ` · ${result.conflicts} conflict${result.conflicts !== 1 ? 's' : ''} (source changed)` : '';
                addNotification(
                    `Write-back done — ${result.synced} synced, ${result.failed} failed${conflictMsg}`,
                    result.failed > 0 ? 'warning' : result.conflicts > 0 ? 'warning' : 'success'
                );
                load();
                return result;
            }
        } catch (e) { addNotification('Write-back failed: ' + (e?.message || ''), 'error'); }
        finally { setWriting(false); }
        return null;
    };

    const handleEditField = async (itemId, fields) => {
        let comment = null;
        // Find the assignee column from schema_map (new array format) or old slot format
        const rawSch = app?.schema_map;
        const assigneeKey = Array.isArray(rawSch)
            ? rawSch.find(r => (r.core_mapping || '').toLowerCase().replace(/\s+/g,'_') === 'assignee')?.source_field
            : rawSch?.slots?.assignee;
        if (assigneeKey && fields[assigneeKey] !== undefined) {
            const currentItem = items.find(i => i.id === itemId);
            const currentVal = currentItem?.resolved?.[assigneeKey] || '';
            const newVal = fields[assigneeKey] || '';
            
            if (String(currentVal) !== String(newVal)) {
                const userComment = window.prompt("A reallocation comment is mandatory when changing the assignee. Please enter a reason:");
                if (userComment === null) {
                    // User clicked cancel, we need to reload the UI to reset dropdown value
                    load();
                    return;
                }
                if (!userComment.trim()) {
                    addNotification("A reallocation comment is required to change the assignee.", "error");
                    load();
                    return;
                }
                comment = userComment.trim();
            }
        }

        try {
            await apiPatch(`/worklists/${appId}/items/${itemId}`, token, { fields, comment });
            
            let updatedAudit = [];
            try {
                updatedAudit = await apiGet(`/worklists/${appId}/items/${itemId}/audit`, token);
            } catch (e) {
                console.error(e);
            }

            setItems(prev => prev.map(i => i.id === itemId ? {
                ...i,
                resolved: { ...i.resolved, ...fields },
                field_overrides: { ...i.field_overrides, ...fields },
                writeback_status: 'pending'
            } : i));
            
            // If the active drawer is open, keep it in sync with new resolved values and audit history
            setDrawer(prev => {
                if (prev && prev.item.id === itemId) {
                    return {
                        ...prev,
                        audit: updatedAudit.length > 0 ? updatedAudit : prev.audit,
                        item: {
                            ...prev.item,
                            resolved: { ...prev.item.resolved, ...fields },
                            field_overrides: { ...prev.item.field_overrides, ...fields },
                            writeback_status: 'pending'
                        }
                    };
                }
                return prev;
            });
        } catch { addNotification('Update failed', 'error'); }
    };

    const handleCreateItem = async () => {
        if (!newItemForm) return;
        try {
            const item = await apiPost(`/worklists/${appId}/items`, token, { fields: newItemForm });
            setItems(prev => [item, ...prev]);
            setNewItemForm(null);
            addNotification('Item created — will be synced on next write-back', 'success');
        } catch { addNotification('Create failed', 'error'); }
    };

    const openDrawer = async (item) => {
        setDrawer({ item, audit: [] });
        try {
            const audit = await apiGet(`/worklists/${appId}/items/${item.id}/audit`, token);
            setDrawer({ item, audit: Array.isArray(audit) ? audit : [] });
        } catch {}
    };

    const handleRollback = async (itemId, field) => {
        if (!window.confirm(`Rollback '${field}' to its original source value?`)) return;
        try {
            const updated = await apiDelete(`/worklists/${appId}/items/${itemId}/overrides/${field}`, token);
            // Refresh item in table and drawer
            setItems(prev => prev.map(i => i.id === itemId ? {
                ...i,
                resolved: updated,
                field_overrides: Object.fromEntries(
                    Object.entries(i.field_overrides || {}).filter(([k]) => k !== field)
                ),
                writeback_status: 'pending'
            } : i));
            setDrawer(prev => prev ? {
                ...prev,
                item: {
                    ...prev.item,
                    resolved: updated,
                    field_overrides: Object.fromEntries(
                        Object.entries(prev.item.field_overrides || {}).filter(([k]) => k !== field)
                    ),
                    writeback_status: 'pending'
                }
            } : prev);
            addNotification(`'${field}' rolled back to source value`, 'success');
        } catch { addNotification('Rollback failed', 'error'); }
    };

    // ── Revert ALL overrides for a single row ──────────────────────────────
    const handleRevertAll = async (itemId) => {
        try {
            const restored = await apiDelete(`/worklists/${appId}/items/${itemId}/overrides`, token);
            setItems(prev => prev.map(i => i.id === itemId ? {
                ...i,
                resolved: restored,
                field_overrides: {},
                writeback_status: 'synced',
            } : i));
            setDrawer(prev => prev?.item?.id === itemId ? {
                ...prev,
                item: { ...prev.item, resolved: restored, field_overrides: {}, writeback_status: 'synced' }
            } : prev);
            setRevertConfirmId(null);
            addNotification('Row reverted to source values', 'success');
        } catch { addNotification('Revert failed', 'error'); }
    };

    // ── Mass Update: batch PATCH in groups of 10, skip+report failures ─────
    const handleMassUpdate = async (fields) => {
        if (!selectedIds.size || !Object.keys(fields).length) return;
        const ids = [...selectedIds];
        let ok = 0, failed = 0;
        const BATCH = 10;
        for (let i = 0; i < ids.length; i += BATCH) {
            const chunk = ids.slice(i, i + BATCH);
            await Promise.all(chunk.map(async (itemId) => {
                try {
                    const updated = await apiPatch(`/worklists/${appId}/items/${itemId}`, token, { fields });
                    setItems(prev => prev.map(it => it.id === itemId ? {
                        ...it,
                        resolved: { ...it.resolved, ...fields },
                        writeback_status: 'pending',
                    } : it));
                    ok++;
                } catch { failed++; }
            }));
        }
        const msg = failed
            ? `${ok} updated, ${failed} failed`
            : `${ok} item${ok !== 1 ? 's' : ''} updated`;
        addNotification(msg, failed ? 'warning' : 'success');
        setMassUpdateOpen(false);
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    if (loading && !app) return (
        <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-slate-950">
            <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-9 h-9 animate-spin text-indigo-650 dark:text-indigo-400" />
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Workspace...</span>
            </div>
        </div>
    );

    // ── Schema parsing: support new flat array format AND old slot-based format ──
    const rawSchema = app?.schema_map;
    const isArraySchema = Array.isArray(rawSchema);

    // ── NEW format: flat array from Step 2 ──────────────────────────────────
    // Each row: { source_field, is_selected, is_read_only, field_type, core_mapping, options }
    let slots = {};
    let statuses = app?.workflow_config?.statuses || ['Open', 'In Progress', 'Closed'];
    let allCols = [];
    let isColEditable = () => false;

    if (isArraySchema) {
        const selectedRows = rawSchema.filter(r => r.is_selected !== false);

        // Derive slots from core_mapping for backward compat features (assignee filter, status dropdown)
        selectedRows.forEach(r => {
            const cm = (r.core_mapping || '').toLowerCase().replace(/\s+/g, '_');
            if (cm === 'status')      slots.status   = r.source_field;
            if (cm === 'primary_key') slots.pk       = r.source_field;
            if (cm === 'title')       slots.title    = r.source_field;
            if (cm === 'assignee')    slots.assignee = r.source_field;
            if (cm === 'due_date')    slots.due_date = r.source_field;
        });

        // Build cols directly from selected rows — order preserved from Step 2
        allCols = selectedRows.map(r => {
            const ft = (r.field_type || 'As-Is').toLowerCase();
            let type = 'text';
            if (ft.includes('drop') || ft.includes('single')) type = 'dropdown';
            else if (ft.includes('multi'))                     type = 'multi';
            else if (ft.includes('date'))                      type = 'date';
            else if (ft.includes('number') || ft.includes('numeric')) type = 'number';
            else if (ft.includes('check') || ft.includes('boolean'))  type = 'checkbox';

            // Status column gets workflow statuses as options
            const isStatusCol = slots.status === r.source_field;
            const opts = isStatusCol ? statuses.join(',') : (r.options || '');

            return {
                key:      r.source_field,
                label:    r.source_field,
                type,
                options:  opts,
                readOnly: !!r.is_read_only,
                slotName: Object.entries(slots).find(([, v]) => v === r.source_field)?.[0] || null,
            };
        });

        const roleId = user?.roleId || '';
        const isAdminRole = ['r1', 'r2', 'r3'].includes(roleId);
        isColEditable = (col) => {
            // is_read_only from Step 2 is ABSOLUTE — not even admins can bypass it.
            // It means the field is display-only (e.g. ID, primary key, source reference).
            // Admin privilege only bypasses record-level terminal-status locks, not field config.
            if (col.readOnly) return false;
            return true;  // Editable for everyone (record-level lock handled separately in WorklistRow)
        };
        // Expose isAdminRole so WorklistRow can bypass record-level lock for admins
        allCols = allCols.map(c => ({ ...c, _isAdminRole: isAdminRole }));

    } else {
        // ── OLD format: slot-based object (backward compat) ─────────────────
        const schema = rawSchema || {};
        slots = schema.slots || {};
        statuses = app?.workflow_config?.statuses || ['Open', 'In Progress', 'Closed'];

        const configuredPool = app?.workflow_config?.assignee_pool || [];
        const assigneePool = configuredPool.length > 0
            ? configuredPool
            : platformUsers.length > 0
                ? platformUsers
                : (() => {
                    const assigneeKey = schema?.slots?.assignee;
                    if (!assigneeKey || !items?.length) return [];
                    return [...new Set(items.map(i => i.resolved?.[assigneeKey]).filter(Boolean))].sort();
                })();

        const slotCols = [
            slots.title    && { key: slots.title,    slotName: 'title',    label: 'Title',    type: 'text' },
            slots.status   && { key: slots.status,   slotName: 'status',   label: 'Status',   type: 'dropdown', options: statuses.join(',') },
            slots.assignee && { key: slots.assignee, slotName: 'assignee', label: 'Assignee', type: 'dropdown', options: assigneePool.join(',') },
            slots.due_date && { key: slots.due_date, slotName: 'due_date', label: 'Due Date', type: 'date' },
            slots.amount   && { key: slots.amount,   slotName: 'amount',   label: 'Amount',   type: 'number' },
            ...(schema.custom_fields || []).map(cf => typeof cf === 'string'
                ? { key: cf, label: cf, type: 'text', isCustom: true }
                : { key: cf.source_col, label: cf.label || cf.source_col, type: cf.type || 'text', options: cf.options || '', isCustom: true }
            )
        ].filter(Boolean);

        // Extra raw source columns not mapped — show read-only
        const mappedKeys = new Set(slotCols.map(c => c.key));
        const firstResolved = items[0]?.resolved || {};
        const extraCols = Object.keys(firstResolved)
            .filter(k => !k.startsWith('__') && !mappedKeys.has(k))
            .map(k => ({ key: k, label: k, type: 'text', isExtra: true, slotName: null }));
        allCols = [...slotCols, ...extraCols];

        const editableSlots  = schema.editable_slots  || ['title', 'status', 'assignee', 'due_date', 'amount', 'notes'];
        const editableCustom = schema.editable_custom || {};
        const roleId = user?.roleId || '';
        const isPrivilegedRole = roleId === 'r1';

        isColEditable = (col) => {
            if (col.isExtra) return false;
            if (isPrivilegedRole) return true;
            if (col.isCustom) {
                if (Array.isArray(editableCustom)) return editableCustom.includes(col.key);
                return (editableCustom[col.key] || []).includes(roleId);
            }
            if (Array.isArray(editableSlots)) return editableSlots.includes(col.slotName);
            return (editableSlots[col.slotName] || []).includes(roleId);
        };
    }

    const configuredPool = app?.workflow_config?.assignee_pool || [];
    const assigneePool = configuredPool.length > 0
        ? configuredPool
        : platformUsers.length > 0
            ? platformUsers
            : (() => {
                const ak = slots.assignee;
                if (!ak || !items?.length) return [];
                return [...new Set(items.map(i => i.resolved?.[ak]).filter(Boolean))].sort();
            })();

    // Apply filters: Assigned to Me + dynamic column filters
    const userEmail = token ? (() => { try { return JSON.parse(atob(token.split('.')[1]))?.email; } catch { return ''; } })() : '';

    // Column filter operator logic
    const applyOp = (cellVal, op, filterVal) => {
        const s = String(cellVal ?? '').toLowerCase();
        const f = String(filterVal ?? '').toLowerCase();
        switch (op) {
            case 'eq':          return s === f;
            case 'neq':         return s !== f;
            case 'contains':    return s.includes(f);
            case 'starts':      return s.startsWith(f);
            case 'gt':          return parseFloat(cellVal) > parseFloat(filterVal);
            case 'lt':          return parseFloat(cellVal) < parseFloat(filterVal);
            case 'empty':       return !cellVal || cellVal === '';
            case 'not_empty':   return !!cellVal && cellVal !== '';
            default:            return true;
        }
    };

    const filteredItems = items.filter(item => {
        // Assigned to Me filter
        if (myItems && slots.assignee) {
            const assigneeVal = item.resolved?.[slots.assignee];
            if (String(assigneeVal || '').toLowerCase() !== String(userEmail || '').toLowerCase()) return false;
        }
        // Dynamic column filters (AND)
        for (const f of columnFilters) {
            if (!f.col) continue;
            // Filter condition based on original source columns
            const cellVal = item.data?.[f.col] ?? item.resolved?.[f.col];
            if (!applyOp(cellVal, f.op, f.value)) return false;
        }
        return true;
    });

    // ── Client-side sort applied on top of filtered results ──────────────────
    const sortedItems = (() => {
        if (!sortCol) return filteredItems;
        return [...filteredItems].sort((a, b) => {
            // Coerce to primitive — Excel numbers come back as JS numbers (e.g. 1001)
            const rawA = a.resolved?.[sortCol] ?? a.data?.[sortCol] ?? '';
            const rawB = b.resolved?.[sortCol] ?? b.data?.[sortCol] ?? '';
            const va = rawA === null || rawA === undefined ? '' : rawA;
            const vb = rawB === null || rawB === undefined ? '' : rawB;
            // Numeric comparison when both values are numbers or numeric strings
            const na = Number(va), nb = Number(vb);
            let cmp;
            if (!isNaN(na) && !isNaN(nb) && va !== '' && vb !== '') {
                cmp = na - nb;
            } else {
                cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
    })();

    const handleSort = (key) => {
        if (sortCol === key) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortCol(null); setSortDir('asc'); }   // third click clears sort
        } else {
            setSortCol(key);
            setSortDir('asc');
        }
    };

    const pendingCount = items.filter(i => i.writeback_status === 'pending').length;

    return (
        <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-950 overflow-hidden font-sans">
            {/* Header banner */}
            <div className="flex-none px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-150 dark:border-slate-800/80 flex items-center justify-between shadow-[0_2px_15px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-3.5">
                    <button onClick={onBack} className="p-2 rounded-xl text-gray-500 hover:text-gray-800 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all hover:scale-102 active:scale-98">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-2xl text-indigo-650 dark:text-indigo-400">
                        <ClipboardList className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-base font-extrabold text-gray-900 dark:text-slate-100 leading-tight tracking-tight">{app?.name}</h1>
                        <p className="text-[11px] font-medium text-gray-400 mt-0.5">{total} items · <span className="font-bold text-amber-600 dark:text-amber-400">{pendingCount} pending sync</span></p>
                    </div>
                </div>
                <div className="flex items-center gap-2">

                    {/* ── Add Entry ── gated by allow_add_entry config */}
                    {!isPreview && (() => {
                        const policy = app?.allow_add_entry || 'all';
                        const canAdd = policy === 'all' || (policy === 'admin' && isAdmin);
                        if (!canAdd) return null;
                        return (
                            <button
                                onClick={() => setNewItemForm({})}
                                className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white
                                    bg-gradient-to-br from-emerald-500 to-teal-600
                                    shadow-[0_2px_8px_rgba(16,185,129,0.30)]
                                    hover:shadow-[0_4px_16px_rgba(16,185,129,0.40)]
                                    hover:-translate-y-px active:translate-y-0
                                    transition-all duration-150">
                                <Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-200" />
                                Add Entry
                            </button>
                        );
                    })()}

                    {/* Divider between Add Entry and admin tools */}
                    {!isPreview && isAdmin && <div className="h-5 w-px bg-gray-200 dark:bg-slate-700" />}

                    {/* ── Pull Data + Sync Changes — admin only */}
                    {!isPreview && isAdmin && (
                        <>
                            <PullMenu onPull={handlePull} loading={pulling} />

                            {/* Sync Changes — hidden when platform-managed */}
                            {app?.writeback_sink?.type !== 'platform' ? (
                                <button
                                    onClick={() => setReviewModal(true)}
                                    disabled={writing || pendingCount === 0}
                                    className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white
                                        bg-gradient-to-br from-violet-600 to-indigo-700
                                        shadow-[0_2px_8px_rgba(99,102,241,0.25)]
                                        hover:shadow-[0_4px_16px_rgba(99,102,241,0.35)]
                                        hover:-translate-y-px active:translate-y-0
                                        transition-all duration-150
                                        disabled:opacity-50 disabled:pointer-events-none disabled:translate-y-0 disabled:shadow-none">
                                    {writing
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Upload className="w-3.5 h-3.5 group-hover:scale-110 transition-transform duration-150" />}
                                    Sync Changes
                                    {pendingCount > 0 && (
                                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/30 text-[9px] font-black leading-none">
                                            {pendingCount > 99 ? '99+' : pendingCount}
                                        </span>
                                    )}
                                </button>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold
                                    bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300
                                    border border-blue-200 dark:border-blue-800/40">
                                    🔒 Platform-managed
                                </span>
                            )}
                        </>
                    )}

                    {isPreview && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold
                            bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400
                            border border-amber-200 dark:border-amber-800/40">
                            👁 Preview — actions disabled
                        </span>
                    )}

                </div>
            </div>


            {/* Quick Filters + Select Mode toolbar */}
            <div className="flex-none px-6 py-3 bg-white/50 dark:bg-slate-900/40 border-b border-gray-150 dark:border-slate-800/50 flex flex-col gap-2">
                {/* Row 1: search + status + my items + select + filter button */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 flex-1 flex-wrap">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input type="text" placeholder="Search entries…" value={searchVal}
                                onChange={e => setSearchVal(e.target.value)}
                                className="pl-9 pr-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all w-48 dark:text-slate-100" />
                        </div>
                        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            className="text-xs px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                            <option value="">All Statuses</option>
                            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {slots.assignee && (
                            <button onClick={() => { setMyItems(v => !v); setPage(1); }}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                                    myItems
                                        ? 'bg-indigo-600 border-indigo-700 text-white'
                                        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-700 dark:text-slate-300 hover:border-indigo-400'
                                }`}>
                                👤 Assigned to Me
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {/* Filters Panel Toggle */}
                        <button onClick={() => setFilterPanelOpen(true)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-gray-300 dark:border-slate-700 text-xs font-bold transition-all ${
                            columnFilters.length > 0 
                                ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-700'
                                : 'bg-white dark:bg-slate-900 text-gray-500 dark:text-slate-400 hover:border-violet-400 hover:text-violet-600'
                        }`}>
                            <Filter className="w-3.5 h-3.5" /> Filters {columnFilters.length > 0 && `(${columnFilters.length})`}
                        </button>
                        {/* Save View — persists current sort to localStorage */}
                        {sortCol && (
                            <button
                                onClick={() => {
                                    try { localStorage.setItem(lsKey, JSON.stringify({ col: sortCol, dir: sortDir })); } catch {}
                                    setViewSaved(true);
                                    setTimeout(() => setViewSaved(false), 1800);
                                }}
                                title={`Save current sort (${sortCol} ${sortDir}) as your default view`}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                                    viewSaved
                                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400 text-emerald-600 dark:text-emerald-400'
                                        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-600'
                                }`}>
                                {viewSaved ? '✓ Saved' : '💾 Save View'}
                            </button>
                        )}
                        {/* Select mode toggle */}
                        {!isPreview && (
                            <button onClick={() => { setSelectionMode(v => !v); setSelectedIds(new Set()); }}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                                    selectionMode
                                        ? 'bg-violet-600 border-violet-700 text-white shadow-sm shadow-violet-500/20'
                                        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-600 dark:text-slate-300 hover:border-violet-400'
                                }`}>
                                <CheckSquare className="w-3.5 h-3.5" />
                                {selectionMode ? `${selectedIds.size} selected` : 'Select'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Empty-data notice — columns in schema but all null = empty in source */}
            {(() => {
                if (!items.length || !isArraySchema) return null;
                const emptyCols = allCols.filter(col =>
                    items.every(it => {
                        const v = it.resolved?.[col.key];
                        return v === null || v === undefined || v === '';
                    })
                );
                if (!emptyCols.length) return null;
                return (
                    <div className="flex-none px-6 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700/40 flex items-center gap-3">
                        <span className="text-amber-600 text-sm">⚠️</span>
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 font-semibold flex-1">
                            <span className="font-extrabold">{emptyCols.map(c => c.label).join(', ')}</span>
                            {' '}— these columns are empty in the source data. Check that your source (SharePoint sheet / SQL table) actually has values in these columns, then <span className="font-extrabold">Pull Data → Upsert</span>.
                        </p>
                    </div>
                );
            })()}

            {/* Main Content Workspace */}
            <div className="flex-1 overflow-auto px-6 py-5 custom-scrollbar">
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-150 dark:border-slate-800/80 overflow-x-auto shadow-sm custom-scrollbar">
                    <table className="min-w-max w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-150 dark:border-slate-850 bg-gray-50/50 dark:bg-slate-900/50">
                                {/* Checkbox select-all column */}
                                {selectionMode && (
                                    <th className="pl-5 pr-2 py-3.5 w-9">
                                        <input type="checkbox"
                                            className="w-4 h-4 rounded accent-violet-600 cursor-pointer"
                                            checked={filteredItems.length > 0 && filteredItems.every(i => selectedIds.has(i.id))}
                                            onChange={e => {
                                                if (e.target.checked) setSelectedIds(new Set(filteredItems.map(i => i.id)));
                                                else setSelectedIds(new Set());
                                            }} />
                                    </th>
                                )}
                                {allCols.map(col => (
                                    <th key={col.key}
                                        onClick={() => handleSort(col.key)}
                                        className={`min-w-[150px] px-5 py-3.5 text-left text-[10px] font-extrabold uppercase tracking-widest cursor-pointer select-none transition-colors ${
                                            sortCol === col.key
                                                ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/40 dark:bg-indigo-950/20'
                                                : col.isExtra ? 'text-gray-300 dark:text-slate-700 hover:text-gray-400' : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300'
                                        }`}>
                                        <span className="flex items-center gap-1 group">
                                            {col.label}
                                            {!isColEditable(col) && !col.isExtra && <span title="Read-only" className="text-gray-300 dark:text-slate-700">🔒</span>}
                                            {sortCol === col.key ? (
                                                sortDir === 'asc'
                                                    ? <span className="text-indigo-500 text-[10px] font-black">▲</span>
                                                    : <span className="text-indigo-500 text-[10px] font-black">▼</span>
                                            ) : (
                                                <span className="text-gray-300 dark:text-slate-700 text-[9px] opacity-0 group-hover:opacity-100 transition-opacity">⇅</span>
                                            )}
                                        </span>
                                    </th>
                                ))}
                                <th className="px-5 py-3.5 text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-widest text-right">Sync State</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-850">
                            {filteredItems.length === 0 ? (
                                <tr>
                                    <td colSpan={allCols.length + (selectionMode ? 2 : 1)} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-2 max-w-sm mx-auto">
                                            <ClipboardList className="w-10 h-10 text-gray-300 dark:text-slate-700" />
                                            <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200">No matching entries</h3>
                                            <p className="text-xs text-gray-400">Pull data from the source, create a new entry manually, or adjust your search filters.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : sortedItems.map(item => (
                                <WorklistRow
                                    key={item.id}
                                    item={item}
                                    cols={allCols}
                                    slots={slots}
                                    statuses={statuses}
                                    terminalStatuses={app?.workflow_config?.terminal_statuses || ['Closed']}
                                    onEdit={handleEditField}
                                    onOpen={() => { if (!selectionMode) openDrawer(item); }}
                                    isColEditable={isColEditable}
                                    selectionMode={selectionMode}
                                    selected={selectedIds.has(item.id)}
                                    onToggleSelect={() => setSelectedIds(prev => {
                                        const next = new Set(prev);
                                        next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                                        return next;
                                    })}
                                    onRevert={handleRevertAll}
                                    revertConfirmId={revertConfirmId}
                                    setRevertConfirmId={setRevertConfirmId}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-4 text-xs font-semibold text-gray-500 dark:text-slate-400">
                        <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} entries</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 disabled:opacity-40 disabled:pointer-events-none hover:bg-gray-50 dark:hover:bg-slate-800/80 transition-all duration-200">Previous</button>
                            <button disabled={page * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} className="px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 disabled:opacity-40 disabled:pointer-events-none hover:bg-gray-50 dark:hover:bg-slate-800/80 transition-all duration-200">Next</button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Floating Selection Action Bar ──────────────────────────────────── */}
            {selectionMode && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3
                    bg-slate-900/95 dark:bg-slate-800/95 backdrop-blur-md text-white
                    px-5 py-3 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.35)]
                    border border-white/10 animate-in slide-in-from-bottom-4 duration-200">
                    {/* Select All filtered */}
                    <button onClick={() => setSelectedIds(new Set(filteredItems.map(i => i.id)))}
                        className="text-[11px] font-bold text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/10">
                        Select All ({filteredItems.length})
                    </button>
                    <div className="h-4 w-px bg-white/20" />
                    <span className="text-[11px] font-bold text-white">
                        {selectedIds.size} record{selectedIds.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="h-4 w-px bg-white/20" />
                    <button onClick={() => setMassUpdateOpen(true)} disabled={selectedIds.size === 0}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-xl text-xs font-bold
                            bg-gradient-to-br from-violet-500 to-indigo-600 text-white
                            shadow-[0_2px_8px_rgba(99,102,241,0.4)]
                            hover:shadow-[0_4px_16px_rgba(99,102,241,0.5)]
                            hover:-translate-y-px active:translate-y-0 transition-all
                            disabled:opacity-50 disabled:pointer-events-none">
                        <Layers className="w-3.5 h-3.5" /> Mass Update
                    </button>
                    <button onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* New Item Modal */}
            {newItemForm !== null && (
                <NewItemModal
                    cols={allCols}
                    statuses={statuses}
                    slots={slots}
                    assigneePool={assigneePool}
                    onSave={handleCreateItem}
                    onClose={() => setNewItemForm(null)}
                    form={newItemForm}
                    setForm={setNewItemForm}
                />
            )}

            {/* Audit & Edit Side Drawer */}
            {drawer && (
                <ItemDrawer
                    item={drawer.item}
                    audit={drawer.audit}
                    slots={slots}
                    cols={allCols}
                    statuses={statuses}
                    assigneePool={assigneePool}
                    onEdit={handleEditField}
                    onRollback={handleRollback}
                    onClose={() => setDrawer(null)}
                    isColEditable={isColEditable}
                />
            )}

            {/* Filter Drawer */}
            <FilterDrawer
                isOpen={filterPanelOpen}
                onClose={() => setFilterPanelOpen(false)}
                cols={allCols}
                filters={columnFilters}
                setFilters={setColumnFilters}
            />

            {/* Sync Review Modal */}
            {reviewModal && (
                <SyncReviewModal
                    items={items.filter(i => i.writeback_status === 'pending')}
                    slots={slots}
                    app={app}
                    writing={writing}
                    onConfirm={async (cfg) => {
                        setReviewModal(false);
                        const result = await handleWriteback(false, cfg);
                        // If conflicts, re-open modal with conflict info so user can force-write
                        if (result?.conflicts > 0) {
                            setReviewModal(true);
                        }
                    }}
                    onDryRun={async (cfg) => {
                        await handleWriteback(true, cfg);
                    }}
                    onClose={() => setReviewModal(false)}
                />
            )}

            {/* Mass Update Modal */}
            {massUpdateOpen && (
                <MassUpdateModal
                    cols={allCols.filter(c => isColEditable(c))}
                    statuses={statuses}
                    selectedCount={selectedIds.size}
                    onApply={handleMassUpdate}
                    onClose={() => setMassUpdateOpen(false)}
                />
            )}
        </div>
    );
}

function SyncReviewModal({ items, slots, app, writing, onConfirm, onDryRun, onClose }) {
    const [showConfig, setShowConfig] = useState(false);
    const [writeMode, setWriteMode]   = useState(app?.writeback_mode || 'sql_table');
    const [forceWrite, setForceWrite] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set(items.map(i => i.id)));

    // Detect items in conflict state (source changed since pull)
    const conflictItems = items.filter(i => i.writeback_status === 'conflict');
    const pendingItems  = items.filter(i => i.writeback_status === 'pending');
    const displayItems  = forceWrite ? [...conflictItems, ...pendingItems] : pendingItems;

    // Collect all unique field names changed across all selected items
    const allChangedFields = [...new Set(
        items.filter(i => selectedIds.has(i.id))
             .flatMap(i => Object.keys(i.field_overrides || {}))
    )];
    const [includedFields, setIncludedFields] = useState(() => new Set(allChangedFields));

    const toggleId = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleField = (f) => setIncludedFields(prev => {
        const next = new Set(prev);
        next.has(f) ? next.delete(f) : next.add(f);
        return next;
    });

    const MODES = [
        { v: 'sql_table',        label: 'SQL Write-back',       desc: 'Write directly to the source database table', icon: '🗄️' },
        { v: 'platform_managed', label: 'Platform-Managed',     desc: 'Keep changes inside the platform only (no source update)', icon: '☁️' },
        { v: 'trigger_flow',     label: 'Trigger Flow',         desc: 'Send changes to a connected Aidas Flow for processing', icon: '⚡' },
    ];

    const selectedCount = selectedIds.size;

    return (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-gray-150 dark:border-slate-800 shadow-[0_25px_80px_rgba(0,0,0,0.2)] w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex-none">
                    <div>
                        <h2 className="text-base font-extrabold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                            <Upload className="w-4 h-4 text-indigo-500" /> Review &amp; Configure Sync
                        </h2>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
                            {selectedCount} of {items.length} item{items.length !== 1 ? 's' : ''} selected · Mode: <span className="font-bold text-indigo-500">{MODES.find(m => m.v === writeMode)?.label}</span>
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Conflict Warning Banner */}
                {conflictItems.length > 0 && (
                    <div className="flex-none px-6 py-3 bg-orange-50 dark:bg-orange-950/30 border-b border-orange-200 dark:border-orange-800/40">
                        <div className="flex items-start gap-3">
                            <span className="text-orange-500 text-base mt-0.5">⚠️</span>
                            <div className="flex-1">
                                <p className="text-xs font-extrabold text-orange-700 dark:text-orange-400">
                                    {conflictItems.length} item{conflictItems.length !== 1 ? 's' : ''} have source conflicts
                                </p>
                                <p className="text-[10px] text-orange-600/80 dark:text-orange-500/80 mt-0.5">
                                    The source database was updated after your last pull. These items were skipped to prevent overwriting newer data.
                                </p>
                            </div>
                            <button onClick={() => setForceWrite(v => !v)}
                                className={`flex-none px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all ${forceWrite ? 'border-orange-500 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30'}`}>
                                {forceWrite ? '✓ Force Write ON' : 'Force Write (override)'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Collapsible Write-back Config */}
                <div className="flex-none border-b border-gray-100 dark:border-slate-800">
                    <button onClick={() => setShowConfig(v => !v)}
                        className="w-full flex items-center justify-between px-6 py-3 text-xs font-bold text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                        <span className="flex items-center gap-2">⚙️ Write-back Configuration</span>
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showConfig ? 'rotate-180' : ''}`} />
                    </button>

                    {showConfig && (
                        <div className="px-6 pb-5 space-y-4 bg-gray-50/40 dark:bg-slate-800/20">
                            {/* Write Mode */}
                            <div>
                                <p className="text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-2">Write-back Mode</p>
                                <div className="grid grid-cols-3 gap-2">
                                    {MODES.map(m => (
                                        <button key={m.v} type="button" onClick={() => setWriteMode(m.v)}
                                            className={`flex flex-col gap-1.5 p-3 rounded-2xl border-2 text-left transition-all ${writeMode === m.v ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300'}`}>
                                            <span className="text-base">{m.icon}</span>
                                            <span className={`text-[11px] font-extrabold ${writeMode === m.v ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-slate-200'}`}>{m.label}</span>
                                            <span className="text-[10px] text-gray-400 dark:text-slate-500 leading-tight">{m.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Fields to Include */}
                            {allChangedFields.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-2">Fields to Write Back</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allChangedFields.map(f => (
                                            <button key={f} type="button" onClick={() => toggleField(f)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all ${includedFields.has(f) ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300' : 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-500 line-through'}`}>
                                                {includedFields.has(f) ? '✓' : '✗'} {f}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1.5">{includedFields.size} of {allChangedFields.length} field types will be written back</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-auto px-6 py-4 custom-scrollbar space-y-3">
                    {displayItems.map(item => {
                        const overrides = item.field_overrides || {};
                        const source    = item.source_row_data || {};
                        const title     = item.resolved?.[slots.title] || item.id;
                        const selected  = selectedIds.has(item.id);
                        const changedFields = Object.entries(overrides).filter(([f]) => includedFields.has(f));

                        const isConflict = item.writeback_status === 'conflict';

                        return (
                            <div key={item.id} className={`border rounded-2xl overflow-hidden transition-all ${selected ? (isConflict ? 'border-orange-300 dark:border-orange-700/60' : 'border-indigo-200 dark:border-indigo-800/60') : 'border-gray-100 dark:border-slate-800 opacity-50'}`}>
                                <div className="px-4 py-2.5 bg-gray-50/60 dark:bg-slate-800/40 flex items-center gap-3">
                                    <input type="checkbox" checked={selected} onChange={() => toggleId(item.id)}
                                        className="w-3.5 h-3.5 rounded accent-indigo-600 cursor-pointer" />
                                    <span className="text-xs font-extrabold text-gray-700 dark:text-slate-200 truncate flex-1">{String(title)}</span>
                                    {isConflict && <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 border border-orange-300 dark:border-orange-700 px-2 py-0.5 rounded-full">⚠ conflict</span>}
                                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 px-2 py-0.5 rounded-full">
                                        {changedFields.length} field{changedFields.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                {selected && changedFields.length > 0 && (
                                    <div className="divide-y divide-gray-50 dark:divide-slate-800/60">
                                        {changedFields.map(([field, newVal]) => {
                                            const oldVal = source[field];
                                            return (
                                                <div key={field} className="grid grid-cols-[140px_1fr_1fr] px-4 py-2 text-xs gap-3 items-center">
                                                    <span className="font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wide text-[10px] truncate">{field}</span>
                                                    <span className="text-red-500 dark:text-red-400 bg-red-50/60 dark:bg-red-950/20 px-2 py-1 rounded-lg truncate line-through opacity-70">
                                                        {oldVal !== undefined && oldVal !== null ? String(oldVal) : '—'}
                                                    </span>
                                                    <span className="text-emerald-600 dark:text-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20 px-2 py-1 rounded-lg truncate font-semibold">
                                                        {newVal !== undefined && newVal !== null ? String(newVal) : '—'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex-none bg-gray-50/40 dark:bg-slate-900/60">
                    <button onClick={() => onDryRun({ writeMode, itemIds: [...selectedIds], fields: [...includedFields] })} disabled={writing || selectedCount === 0}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all disabled:opacity-50">
                        🔍 Dry Run Preview
                    </button>
                    <div className="flex-1" />
                    <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all">
                        Cancel
                    </button>
                    <button onClick={() => onConfirm({ writeMode, itemIds: [...selectedIds], fields: [...includedFields], forceWrite })}
                        disabled={writing || selectedCount === 0}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold shadow-md transition-all hover:scale-102 active:scale-98 disabled:opacity-50 disabled:pointer-events-none text-white ${
                            forceWrite
                                ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-500/15'
                                : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-indigo-500/15'
                        }`}>
                        {writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {forceWrite ? 'Force Sync ' : 'Sync '}{selectedCount} Item{selectedCount !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}

function WorklistRow({
    item, cols, slots, statuses, terminalStatuses, onEdit, onOpen, isColEditable,
    selectionMode, selected, onToggleSelect, onRevert, revertConfirmId, setRevertConfirmId
}) {
    const [drafts, setDrafts] = useState({});
    const resolved = item.resolved || {};
    const statusCol = slots?.status;
    const isPending  = item.writeback_status === 'pending';
    const isAdminRole = cols?.[0]?._isAdminRole ?? false;
    const isLocked   = !isAdminRole && terminalStatuses?.includes(resolved[statusCol]);
    const liveVal = (key) => drafts[key] !== undefined ? drafts[key] : (resolved[key] ?? '');
    const commit = (key, val) => {
        setDrafts(d => { const n = { ...d }; delete n[key]; return n; });
        if (String(val) !== String(resolved[key] ?? '')) onEdit(item.id, { [key]: val });
    };
    const cellCls = 'text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10 transition-all w-full';
    const isConfirmingRevert = revertConfirmId === item.id;

    return (
        <tr className={`border-b border-gray-50 dark:border-slate-800/50 transition-colors group cursor-pointer
            ${selected ? 'bg-violet-50/60 dark:bg-violet-950/20' : 'hover:bg-indigo-50/20 dark:hover:bg-indigo-950/10'}`}
            onClick={selectionMode ? onToggleSelect : onOpen}>
            {/* Checkbox cell */}
            {selectionMode && (
                <td className="pl-5 pr-2 py-2.5" onClick={e => { e.stopPropagation(); onToggleSelect(); }}>
                    <input type="checkbox" checked={!!selected} onChange={() => {}} onClick={e => e.stopPropagation()}
                        className="w-4 h-4 rounded accent-violet-600 cursor-pointer" />
                </td>
            )}
            {cols.map(col => {
                const raw = resolved[col.key];
                const editable = !isLocked && isColEditable(col);

                // ── Read-only cell ──────────────────────────────────────────
                if (!editable) {
                    return (
                        <td key={col.key} className="px-4 py-2.5 text-gray-700 dark:text-slate-300 truncate max-w-[180px] text-xs font-medium">
                            {raw !== undefined && raw !== null && raw !== ''
                                ? String(raw)
                                : <span className="text-gray-300 dark:text-slate-600">—</span>}
                        </td>
                    );
                }

                const stop = e => e.stopPropagation();

                // ── Dropdown ────────────────────────────────────────────────
                if (col.type === 'dropdown' || col.type === 'multi') {
                    const opts = (col.options || '').split(',').map(o => o.trim()).filter(Boolean);
                    return (
                        <td key={col.key} className="px-3 py-2" onClick={stop}>
                            <select value={raw || ''}
                                onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                className={cellCls}>
                                <option value="">— select —</option>
                                {opts.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </td>
                    );
                }

                // ── Date ────────────────────────────────────────────────────
                if (col.type === 'date') {
                    return (
                        <td key={col.key} className="px-3 py-2" onClick={stop}>
                            <input type="date" value={liveVal(col.key)}
                                className={cellCls}
                                onChange={e => setDrafts(d => ({ ...d, [col.key]: e.target.value }))}
                                onBlur={e => commit(col.key, e.target.value)} />
                        </td>
                    );
                }

                // ── Number ──────────────────────────────────────────────────
                if (col.type === 'number') {
                    return (
                        <td key={col.key} className="px-3 py-2" onClick={stop}>
                            <input type="number" value={liveVal(col.key)}
                                className={cellCls}
                                onChange={e => setDrafts(d => ({ ...d, [col.key]: e.target.value }))}
                                onBlur={e => commit(col.key, e.target.value)} />
                        </td>
                    );
                }

                // ── Checkbox / Boolean ──────────────────────────────────────
                if (col.type === 'checkbox') {
                    return (
                        <td key={col.key} className="px-4 py-2 text-center" onClick={stop}>
                            <input type="checkbox" checked={!!raw}
                                className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                                onChange={e => onEdit(item.id, { [col.key]: e.target.checked })} />
                        </td>
                    );
                }

                // ── Default: Text Box (Assignee, Remarks, etc.) ─────────────
                return (
                    <td key={col.key} className="px-3 py-2" onClick={stop}>
                        <input type="text" value={liveVal(col.key)}
                            placeholder={col.label}
                            className={cellCls}
                            onChange={e => setDrafts(d => ({ ...d, [col.key]: e.target.value }))}
                            onBlur={e => commit(col.key, e.target.value)} />
                    </td>
                );
            })}
            <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1.5">
                    {isLocked && <span title="Record locked" className="text-xs text-gray-400">🔒</span>}
                    {/* Sync status badge */}
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border transition-all ${
                        isPending
                            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                            : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                    }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isPending ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
                        {isPending ? 'pending' : 'synced'}
                    </span>
                    {/* Inline Revert — only when pending */}
                    {isPending && !isConfirmingRevert && (
                        <button
                            title="Discard changes — revert to source"
                            onClick={() => setRevertConfirmId(item.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 px-2 py-1 rounded-lg
                                text-[10px] font-bold text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20
                                border border-transparent hover:border-red-200 dark:hover:border-red-800/40">
                            <RotateCcw className="w-2.5 h-2.5" /> Revert
                        </button>
                    )}
                    {/* Inline confirm: replaces button */}
                    {isPending && isConfirmingRevert && (
                        <span className="inline-flex items-center gap-1 animate-in fade-in duration-150">
                            <button onClick={() => onRevert(item.id)}
                                className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-red-500 text-white hover:bg-red-600 transition-colors">
                                Yes, revert
                            </button>
                            <button onClick={() => setRevertConfirmId(null)}
                                className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                                Cancel
                            </button>
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );
}


function PullMenu({ onPull, loading }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button onClick={() => setOpen(v => !v)} disabled={loading}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-white dark:bg-slate-900 border border-gray-250 dark:border-slate-800 hover:border-gray-350 dark:hover:border-slate-700 rounded-xl text-xs font-extrabold text-gray-700 dark:text-slate-200 transition-all disabled:opacity-60 hover:scale-102 active:scale-98">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Pull Data <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1.5 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-2xl border border-gray-150 dark:border-slate-800 shadow-xl py-1.5 min-w-[190px] animate-slide-in" onMouseLeave={() => setOpen(false)}>
                    {[
                        ['upsert', 'Upsert (safe)', 'Update source changes & add new entries'],
                        ['insert_only', 'Insert new only', 'Add newly imported entries only'],
                        ['full_replace', 'Full replace ⚠️', 'Wipe current worklist and rebuild']
                    ].map(([mode, label, desc]) => (
                        <button key={mode} onClick={() => { onPull(mode); setOpen(false); }}
                            className="w-full text-left px-4 py-2 hover:bg-indigo-50/50 dark:hover:bg-slate-800/80 transition-colors flex flex-col gap-0.5">
                            <span className="text-xs font-bold text-gray-800 dark:text-slate-200">{label}</span>
                            <span className="text-[9px] text-gray-400 leading-normal">{desc}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function NewItemModal({ cols, statuses, slots, assigneePool, form, setForm, onSave, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-150 dark:border-slate-800 w-full max-w-md p-6 space-y-5 animate-slide-up">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800/80 pb-3">
                    <h3 className="font-extrabold text-gray-900 dark:text-slate-100 text-base leading-tight tracking-tight">Add New Entry</h3>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-4 max-h-80 overflow-y-auto pr-1.5 custom-scrollbar">
                    {cols.map(col => (
                        <div key={col.key} className="space-y-1.5">
                            <label className="block text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-wide">{col.label}</label>
                            {col.key === slots.status ? (
                                <select value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))}
                                    className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                    <option value="">— Select Status —</option>
                                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            ) : col.key === slots.assignee ? (
                                assigneePool.length > 0 ? (
                                    <select value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))}
                                        className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                        <option value="">— Select Assignee —</option>
                                        {assigneePool.map(a => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                ) : (
                                    <input className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                                )
                            ) : col.type === 'dropdown' ? (
                                <select value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))}
                                    className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                    <option value="">— Select Option —</option>
                                    {(col.options || '').split(',').map(o => o.trim()).filter(Boolean).map(o => (
                                        <option key={o} value={o}>{o}</option>
                                    ))}
                                </select>
                            ) : col.type === 'textarea' ? (
                                <textarea rows={3} className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all resize-none"
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            ) : col.type === 'checkbox' ? (
                                <div className="flex items-center gap-2 pt-1">
                                    <input type="checkbox" checked={!!form[col.key]} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.checked }))}
                                        className="rounded border-gray-300 text-indigo-650 focus:ring-indigo-500 h-4.5 w-4.5 bg-white dark:bg-slate-950" />
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-300">{form[col.key] ? 'Checked' : 'Unchecked'}</span>
                                </div>
                            ) : col.type === 'date' ? (
                                <input type="date" className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            ) : col.type === 'number' ? (
                                <input type="number" className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            ) : (
                                <input className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                    value={form[col.key] || ''} onChange={e => setForm(f => ({ ...f, [col.key]: e.target.value }))} />
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex gap-3 pt-3">
                    <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">Cancel</button>
                    <button onClick={onSave} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-650 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold transition-all shadow-md shadow-indigo-500/10">
                        <Save className="w-4 h-4" /> Save Entry
                    </button>
                </div>
            </div>
        </div>
    );
}

function ItemDrawer({ item, audit, slots, cols, statuses, assigneePool, onEdit, onRollback, onClose, isColEditable }) {
    const resolved = item.resolved || {};
    const overrides = item.field_overrides || {};
    const colKeys = cols.map(c => c.key);
    const extraFields = Object.entries(resolved).filter(([k]) => !k.startsWith('__') && !colKeys.includes(k));

    // Check if record is locked (status is terminal) — backend enforces, frontend just shows UI hint
    const statusVal = resolved[slots.status];
    const isLocked = item._locked === true;

    return (
        <div className="fixed inset-y-0 right-0 z-50 w-[420px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-l border-gray-200 dark:border-slate-800/80 shadow-[0_0_50px_rgba(0,0,0,0.15)] flex flex-col transition-all duration-300 ease-out transform animate-slide-in">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800/80 flex-none bg-gray-50/50 dark:bg-slate-900/50">
                <div>
                    <h3 className="font-extrabold text-gray-900 dark:text-slate-100 truncate max-w-[280px] text-base leading-tight tracking-tight">{resolved[slots.title] || 'Item Detail'}</h3>
                    <p className="text-[10px] font-mono text-gray-400 dark:text-slate-500 mt-1 uppercase tracking-wider">Record ID: {item.id}</p>
                </div>
                <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                    <X className="w-4 h-4" />
                </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-7 custom-scrollbar">
                {/* Interactive Edit Form */}
                <div className="space-y-4">
                    <p className="text-[10px] font-extrabold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Interactive Fields</p>
                    {cols.map(col => {
                        const val = resolved[col.key];
                        const editable = isColEditable(col);
                        if (!editable) {
                            return (
                                <div key={col.key} className="space-y-1.5 bg-gray-50/10 dark:bg-slate-900/10 p-3.5 rounded-2xl border border-gray-100/50 dark:border-slate-800/40 opacity-80">
                                    <label className="block text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center justify-between">
                                        <span>{col.label}</span>
                                        <span className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1">🔒 Read-only</span>
                                    </label>
                                    <div className="text-xs font-semibold text-gray-700 dark:text-slate-350 px-1 py-0.5">
                                        {val !== undefined && val !== null ? String(val) : '—'}
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div key={col.key} className="space-y-1.5 bg-gray-50/30 dark:bg-slate-900/30 p-3.5 rounded-2xl border border-gray-100 dark:border-slate-800/60 transition-all duration-300 hover:border-gray-200 dark:hover:border-slate-700">
                                <label className="block text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center justify-between">
                                    <span>{col.label}</span>
                                    {overrides[col.key] !== undefined && (
                                        <button onClick={() => onRollback(item.id, col.key)}
                                            title="Rollback to original source value"
                                            className="text-[9px] font-bold text-amber-500 hover:text-amber-600 uppercase tracking-widest flex items-center gap-0.5 transition-colors">
                                            ↩ Rollback
                                        </button>
                                    )}
                                </label>
                                {col.key === slots.status ? (
                                    <select value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : col.key === slots.assignee ? (
                                    assigneePool.length > 0 ? (
                                        <select value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                            className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                            <option value="">— Unassigned —</option>
                                            {assigneePool.map(a => <option key={a} value={a}>{a}</option>)}
                                        </select>
                                    ) : (
                                        <input className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                            value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                    )
                                ) : col.type === 'dropdown' ? (
                                    <select value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                        <option value="">— Select —</option>
                                        {(col.options || '').split(',').map(o => o.trim()).filter(Boolean).map(o => (
                                            <option key={o} value={o}>{o}</option>
                                        ))}
                                    </select>
                                ) : col.type === 'textarea' ? (
                                    <textarea rows={3} className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all resize-none"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : col.type === 'checkbox' ? (
                                    <div className="flex items-center gap-2 pt-1">
                                        <input type="checkbox" checked={!!val} onChange={e => onEdit(item.id, { [col.key]: e.target.checked })}
                                            className="rounded border-gray-300 text-indigo-650 focus:ring-indigo-500 h-4.5 w-4.5 bg-white dark:bg-slate-955" />
                                        <span className="text-xs font-medium text-gray-700 dark:text-slate-300">{val ? 'Checked' : 'Unchecked'}</span>
                                    </div>
                                ) : col.type === 'date' ? (
                                    <input type="date" className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : col.type === 'number' ? (
                                    <input type="number" className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : (
                                    <input className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Additional / System Data */}
                {extraFields.length > 0 && (
                    <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-slate-800">
                        <p className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">System / Source Data</p>
                        <div className="space-y-2 bg-gray-50/30 dark:bg-slate-900/50 p-3 rounded-xl border border-gray-100/50 dark:border-slate-800/40">
                            {extraFields.map(([k, v]) => (
                                <div key={k} className="flex flex-col gap-0.5 text-xs">
                                    <span className="font-semibold text-gray-400 dark:text-slate-500 uppercase text-[9px]">{k}</span>
                                    <span className="text-gray-700 dark:text-slate-300 break-all">{String(v ?? '—')}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Audit Trail */}
                {audit.length > 0 && (
                    <div className="pt-5 border-t border-gray-200 dark:border-slate-800/80">
                        <p className="text-[10px] font-extrabold text-gray-450 dark:text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                            <History className="w-3.5 h-3.5 text-indigo-500" /> System Audit Trail
                        </p>
                        <div className="relative pl-4 border-l-2 border-gray-200 dark:border-slate-800 space-y-4">
                            {audit.slice(0, 15).map((a, idx) => (
                                <div key={a.id} className="relative group text-xs">
                                    {/* Timeline dot */}
                                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-indigo-500 ring-4 ring-white dark:ring-slate-900 group-hover:scale-125 transition-transform" />
                                    
                                    <div className="bg-gray-50/60 dark:bg-slate-800/50 rounded-2xl border border-gray-100/80 dark:border-slate-800/50 p-3.5 space-y-1.5 shadow-sm transition-all duration-300 hover:shadow-md hover:border-gray-200 dark:hover:border-slate-700">
                                        <div className="flex justify-between text-[10px] text-gray-400 dark:text-slate-500">
                                            <span className="font-extrabold uppercase text-indigo-600 dark:text-indigo-400 tracking-wider">{a.field_name}</span>
                                            <span>{new Date(a.changed_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed">
                                            {a.old_value ? (
                                                <span className="line-through text-gray-400">{a.old_value}</span>
                                            ) : (
                                                <span className="italic text-gray-300 dark:text-slate-600">None</span>
                                            )}
                                            <span className="mx-1 text-gray-400">→</span>
                                            <span className="font-bold text-gray-800 dark:text-slate-100 bg-gray-250 dark:bg-slate-800/80 px-1.5 py-0.5 rounded-md font-mono text-[11px]">{a.new_value}</span>
                                            
                                            {a.comment && (
                                                <div className="mt-2 p-2 bg-indigo-50/50 dark:bg-indigo-950/20 border-l-2 border-indigo-500 rounded-lg text-[10px] text-indigo-800 dark:text-indigo-300 italic font-medium leading-normal">
                                                    "{a.comment}"
                                                </div>
                                            )}
                                            
                                            {a.changed_by && (
                                                <span className="text-gray-400 dark:text-slate-500 text-[9px] block mt-1 uppercase font-bold tracking-wider">
                                                    by {a.changed_by}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


// ── Mass Update Modal ──────────────────────────────────────────────────────────
function MassUpdateModal({ cols, statuses, selectedCount, onApply, onClose }) {
    const [applying, setApplying] = useState(false);
    const [fieldUpdates, setFieldUpdates] = useState([]);
    const [addKey, setAddKey] = useState('');

    const addField = () => {
        if (!addKey || fieldUpdates.find(f => f.colKey === addKey)) return;
        setFieldUpdates(prev => [...prev, { colKey: addKey, value: '' }]);
        setAddKey('');
    };

    const handleApply = async () => {
        if (!fieldUpdates.length) return;
        const fields = Object.fromEntries(fieldUpdates.map(f => [f.colKey, f.value]));
        setApplying(true);
        await onApply(fields);
        setApplying(false);
    };

    const colByKey = (key) => cols.find(c => c.key === key);

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
            <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-150 dark:border-slate-800 overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-slate-800">
                    <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                        <Layers className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-sm font-extrabold text-gray-900 dark:text-slate-100">Mass Update</h2>
                        <p className="text-[11px] text-gray-400">{selectedCount} record{selectedCount !== 1 ? 's' : ''} will be updated</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Field list */}
                <div className="px-6 py-4 space-y-3 max-h-[320px] overflow-y-auto">
                    {fieldUpdates.length === 0 && (
                        <p className="text-[11px] text-gray-400 text-center py-4">No fields added. Use the picker below to add fields to update.</p>
                    )}
                    {fieldUpdates.map((fu, idx) => {
                        const col = colByKey(fu.colKey);
                        const opts = col?.type === 'dropdown' ? (col.options || '').split(',').map(o => o.trim()).filter(Boolean) : [];
                        return (
                            <div key={fu.colKey} className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-600 dark:text-slate-300 w-28 shrink-0 truncate">{col?.label || fu.colKey}</span>
                                {col?.type === 'dropdown' ? (
                                    <select value={fu.value}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400">
                                        <option value="">— select —</option>
                                        {opts.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : col?.type === 'date' ? (
                                    <input type="date" value={fu.value}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400" />
                                ) : col?.type === 'number' ? (
                                    <input type="number" value={fu.value}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400" />
                                ) : (
                                    <input type="text" value={fu.value} placeholder={`New ${col?.label || fu.colKey} value…`}
                                        onChange={e => setFieldUpdates(prev => prev.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))}
                                        className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400" />
                                )}
                                <button onClick={() => setFieldUpdates(prev => prev.filter((_, i) => i !== idx))}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                    {/* Add field picker */}
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-slate-800">
                        <select value={addKey} onChange={e => setAddKey(e.target.value)}
                            className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-violet-400">
                            <option value="">+ Add a field to update…</option>
                            {cols.filter(c => !fieldUpdates.find(f => f.colKey === c.key)).map(c => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                        <button onClick={addField} disabled={!addKey}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:pointer-events-none transition-all">
                            Add
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/60">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        Cancel
                    </button>
                    <div className="flex-1" />
                    <button onClick={handleApply} disabled={applying || !fieldUpdates.length}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-extrabold text-white
                            bg-gradient-to-br from-violet-600 to-indigo-700
                            shadow-[0_2px_8px_rgba(99,102,241,0.3)]
                            hover:shadow-[0_4px_16px_rgba(99,102,241,0.4)]
                            hover:-translate-y-px active:translate-y-0 transition-all
                            disabled:opacity-50 disabled:pointer-events-none">
                        {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Apply to {selectedCount} record{selectedCount !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}


// ── Filter Drawer — right side panel for advanced filtering ────────────────────────
function FilterDrawer({ isOpen, onClose, cols, filters, setFilters }) {
    const [drafts, setDrafts] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setDrafts(filters.length > 0 ? [...filters] : [{ id: Date.now(), col: '', op: 'contains', value: '' }]);
        }
    }, [isOpen, filters]);

    const OPS = [
        { v: 'contains',  l: 'Contains' },
        { v: 'eq',        l: 'Equals' },
        { v: 'neq',       l: 'Not equal' },
        { v: 'starts',    l: 'Starts with' },
        { v: 'gt',        l: 'Greater than' },
        { v: 'lt',        l: 'Less than' },
        { v: 'empty',     l: 'Is empty' },
        { v: 'not_empty', l: 'Is not empty' },
    ];

    const addDraft = () => setDrafts(ds => [...ds, { id: Date.now(), col: '', op: 'contains', value: '' }]);
    const updateDraft = (id, key, val) => setDrafts(ds => ds.map(d => d.id === id ? { ...d, [key]: val } : d));
    const removeDraft = (id) => setDrafts(ds => ds.filter(d => d.id !== id));

    const apply = () => {
        const valid = drafts.filter(d => d.col);
        setFilters(valid);
        onClose();
    };

    const clearAll = () => {
        setDrafts([{ id: Date.now(), col: '', op: 'contains', value: '' }]);
        setFilters([]);
    };

    return (
        <div className={`fixed inset-y-0 right-0 w-[420px] bg-white dark:bg-slate-950 border-l border-gray-150 dark:border-slate-800/80 shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            {/* Header */}
            <div className="flex-none px-6 py-5 border-b border-gray-150 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-950">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-xl">
                        <Filter className="w-4 h-4" />
                    </div>
                    <h2 className="text-sm font-extrabold text-gray-900 dark:text-slate-100 tracking-tight">Advanced Filters</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={clearAll} className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                        Clear All
                    </button>
                    <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar flex flex-col gap-4">
                {drafts.map((d) => {
                    const needsValue = !['empty', 'not_empty'].includes(d.op);
                    const colDef = cols.find(c => c.key === d.col);
                    const colOpts = colDef?.type === 'dropdown' ? (colDef.options || '').split(',').map(o => o.trim()).filter(Boolean) : [];

                    return (
                        <div key={d.id} className="p-4 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/30 flex flex-col gap-3 relative group transition-all hover:border-violet-300 dark:hover:border-violet-700/50">
                            {/* Remove button */}
                            {drafts.length > 1 && (
                                <button onClick={() => removeDraft(d.id)} className="absolute -top-2.5 -right-2.5 w-6 h-6 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-400 hover:text-red-500 hover:border-red-200 shadow-sm opacity-0 group-hover:opacity-100 transition-all">
                                    <X className="w-3 h-3" />
                                </button>
                            )}

                            {/* Row 1: Column & Operator */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Column</label>
                                    <select value={d.col} onChange={e => updateDraft(d.id, 'col', e.target.value)}
                                        className="w-full text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 transition-all shadow-sm">
                                        <option value="">Select column…</option>
                                        {cols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Condition</label>
                                    <select value={d.op} onChange={e => { updateDraft(d.id, 'op', e.target.value); updateDraft(d.id, 'value', ''); }}
                                        className="w-full text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 transition-all shadow-sm">
                                        {OPS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Value */}
                            {needsValue && (
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Value</label>
                                    {colOpts.length > 0 ? (
                                        <select value={d.value} onChange={e => updateDraft(d.id, 'value', e.target.value)}
                                            className="w-full text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 transition-all shadow-sm">
                                            <option value="">— any —</option>
                                            {colOpts.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : colDef?.type === 'date' ? (
                                        <input type="date" value={d.value} onChange={e => updateDraft(d.id, 'value', e.target.value)}
                                            className="w-full text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 transition-all shadow-sm" />
                                    ) : (
                                        <input type="text" placeholder="Filter value…" value={d.value} onChange={e => updateDraft(d.id, 'value', e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && apply()}
                                            className="w-full text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 transition-all shadow-sm" />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                
                <button onClick={addDraft} className="w-full py-2.5 mt-2 rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-800 text-xs font-bold text-gray-500 dark:text-slate-400 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-all flex items-center justify-center gap-2">
                    <Plus className="w-3.5 h-3.5" /> Add Condition
                </button>
            </div>

            {/* Footer */}
            <div className="flex-none px-6 py-4 border-t border-gray-150 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50">
                <button onClick={apply}
                    className="w-full py-3 rounded-xl text-xs font-extrabold text-white bg-gradient-to-br from-violet-600 to-indigo-700 hover:-translate-y-px active:translate-y-0 transition-all shadow-md hover:shadow-lg hover:shadow-indigo-500/20">
                    Apply {drafts.filter(d => d.col).length} Filter{drafts.filter(d => d.col).length !== 1 ? 's' : ''}
                </button>
            </div>
        </div>
    );
}
